import { Pressable, ScrollView, View } from "react-native";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useFetchFaqsQuery,
  useFetchShopPagePhaseTwoQuery,
} from "queries/cms.queries";
import TenantLogo from "components/TenantLogo";
import Header from "components/Header";
import Footer from "components/Footer";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import HeroSection from "./HeroSection";
import StepOne from "./StepOne";
import StepTwo from "./StepTwo";
import { ShopScreenProps } from "navigation/types";
import { useAppSelector } from "hooks/useAppSelector";
import StepThree from "./StepThree";
import StepFour from "./StepFour";
import HiddenView from "components/HiddenView";
import OrderSummary from "./components/OrderSummary";
import Container from "components/Container";
import Chevron from "components/Chevron";
import { Text } from "@ui-kitten/components";
import {
  resetShopPagePhaseTwo,
  setStep,
} from "features/slices/shopPagePhaseTwoSlice";
import { useAppDispatch } from "hooks/useAppDispatch";
import {
  createPrimaryUser,
  createShopPageAccount,
  resetBuyFlow,
  setIsEditingPrimaryUser,
  setUserCreated,
} from "features/slices/buyFlowSlice";
import { getSanityImage } from "lib/sanity";
import Spacer from "components/Spacer";
import Button from "components/Button";
import { toDashboard, toGiftPage } from "utils/navigationUtils";
import ReactGA from "react-ga4";
import { useMutation } from "@tanstack/react-query";
import { trackConversion } from "queries/conversion.queries";
import { planIdProperty } from "./helpers";
import {
  StripeTokenError,
  StripeTokenResponse,
} from "components/StripePaymentForm/StripePaymentForm";
import toast from "utils/toast";
import useSendConversionData from "hooks/useSendConversionData";
import useTenantUserFields from "hooks/useTenantUserFields";
import usePlanPackageUserFields from "hooks/usePlanPackageUserFields";
import useAuth from "hooks/useAuth";
import FullScreenSpinner from "components/FullScreenSpinner";

const ShopPhaseTwo = ({ route, navigation }: ShopScreenProps) => {
  const { step, signupCharges, selectedCmsPlan, quantity } = useAppSelector(
    (state) => state.shopPagePhaseTwo
  );
  const { userCreated, selectedBillingInterval, loading, primaryUser } =
    useAppSelector((state) => state.buyFlow);
  const { slug } = route.params;

  const stepThreeRef = useRef<HTMLDivElement>();

  const { data: shopPage, isFetching: fetchingShopPage } =
    useFetchShopPagePhaseTwoQuery({ slug });
  const { data: faqs } = useFetchFaqsQuery({ slug, type: "shopPagesPhaseTwo" });
  const { data: branding } = useFetchTenantBrandingQuery();

  const sendConversionData = useSendConversionData();
  const { mutate: track } = useMutation(trackConversion);
  const { tenantUserFields } = useTenantUserFields();
  const { planPackageUserFields } = usePlanPackageUserFields(
    selectedCmsPlan?.[planIdProperty]
  );

  const planPackageSignupCharge = signupCharges.find(
    (charge) =>
      charge.billingInterval === selectedBillingInterval &&
      charge.planPackageId === selectedCmsPlan?.[planIdProperty]
  );

  const dispatch = useAppDispatch();

  const auth = useAuth();

  const getSeatMapUrl = useMemo(() => {
    if (!selectedCmsPlan) return undefined;

    const seatMapName = selectedCmsPlan.seatMapName;
    const assetRef = shopPage?.seatMaps.find(
      (seatMap) => seatMap.name === seatMapName
    )?.image.asset._ref;

    if (!assetRef) return undefined;

    return getSanityImage(assetRef);
  }, [selectedCmsPlan, shopPage]);

  const createAccount = async (token?: StripeTokenResponse) => {
    let userCreateResult;
    // meaning user is making a checkout attempt the first time
    if (!userCreated) {
      // create primary user via /user/register endpoint
      userCreateResult = await dispatch(
        createPrimaryUser({
          tenantUserFields: tenantUserFields!,
          planPackageUserFields: planPackageUserFields!,
        })
      ).unwrap();
      dispatch(setUserCreated(true));
    }
    // create account
    await dispatch(
      createShopPageAccount({
        billingToken: token,
        quantity,
        planPackageId: selectedCmsPlan![planIdProperty]!,
        tenantUserFields: tenantUserFields!,
        planPackageUserFields: planPackageUserFields!,
      })
    );
    sendConversionData(planPackageSignupCharge!.signupCharge, quantity, token!);
    track({
      step: "PURCHASE_COMPLETE",
      flow: "SHOP_PHASE_TWO",
      userId: userCreated ? primaryUser.id : userCreateResult?.id,
    });
  };

  const handleTokenReceived = async (
    error: StripeTokenError | undefined,
    response?: StripeTokenResponse
  ) => {
    if (error) {
      toast.error(error.error.message);
      return;
    }
    createAccount(response);
  };

  useEffect(() => {
    if (shopPage) {
      navigation.setOptions({ title: shopPage.name });
      ReactGA.send({ hitType: "pageview", page: shopPage.name });
      track({ step: "PAGE_LOAD", flow: "SHOP_PHASE_TWO" });
    }
  }, [shopPage]);

  // After user purchases a plan, redirect to dashboard
  useEffect(() => {
    if (!auth.isLoggedIn) return;
    if (auth.isLoggedIn && userCreated) {
      dispatch(resetBuyFlow());
      dispatch(resetShopPagePhaseTwo());
      toDashboard();
    }
  }, [auth, userCreated]);

  const scrollToNext = () => {
    // scroll to step three ref
    setTimeout(() => {
      stepThreeRef.current?.scrollIntoView({
        behavior: "smooth",
      });
    });
  };

  if (fetchingShopPage)
    return (
      <View className=" h-screen w-screen grid place-items-center">
        <View className="animate-bounce">
          <TenantLogo />
        </View>
      </View>
    );

  if (!shopPage || branding?.subdomain !== shopPage.subdomain) {
    window.location.href = "/login";
    return null;
  }

  return (
    <ScrollView className="flex" style={{ backgroundColor: "#f5f5f5" }}>
      {loading && <FullScreenSpinner text="Creating your account..." />}

      {/* Header */}
      <Header
        programName={shopPage.name}
        showLogin
        textStyle={{
          color: branding?.secondaryColor,
        }}
      />
      <View className="flex-grow z-10">
        {step === 1 && (
          <>
            <HeroSection shopPage={shopPage} />
            <StepOne shopPage={shopPage} faqs={faqs} />
          </>
        )}
        {step > 1 && (
          <Container className="relative pt-4 mb-16">
            <Pressable
              onPress={() => {
                dispatch(setStep(1));
                dispatch(setIsEditingPrimaryUser(true));
              }}
              className="mt-2 mb-6 flex-row gap-2 items-center w-max"
            >
              <Chevron direction="left" />
              <Text>Go Back</Text>
            </Pressable>
            <View className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start relative">
              <View className="space-y-4">
                <HiddenView shouldHide={step < 2}>
                  <StepTwo shopPage={shopPage} scrollToNext={scrollToNext} />
                </HiddenView>
                <HiddenView shouldHide={step < 3}>
                  <StepThree ref={stepThreeRef} createAccount={createAccount} />
                </HiddenView>
                <HiddenView shouldHide={step < 4}>
                  {signupCharges.length > 0 && (
                    <StepFour handleTokenReceived={handleTokenReceived} />
                  )}
                </HiddenView>
              </View>
              <View className="hidden md:block sticky top-2 space-y-10">
                <img src={getSeatMapUrl} />
                <OrderSummary />
                {step === 2 && (
                  <Spacer>
                    <Button
                      onPress={() => {
                        dispatch(setStep(step + 1));
                        scrollToNext();
                      }}
                      style={{
                        marginTop: 24,
                        marginBottom: 36,
                        width: 300,
                        alignSelf: "center",
                      }}
                    >
                      CONTINUE
                    </Button>
                    {selectedCmsPlan?.giftingEnabled && (
                      <a
                        onClick={() =>
                          toGiftPage(selectedCmsPlan[planIdProperty])
                        }
                        className="text-center cursor-pointer text-xl font-semibold"
                      >
                        Sending as a gift?
                      </a>
                    )}
                  </Spacer>
                )}
              </View>
            </View>
          </Container>
        )}
      </View>
      <Footer />
    </ScrollView>
  );
};

export default ShopPhaseTwo;
